import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import { usePayrollStore } from "store/payrollStore";
import financeService from "../../../services/finance.service";
import {
  Search, FileText, Download, Filter,
  ChevronRight, AlertCircle, Calendar,
  MoreHorizontal, Loader2, Banknote, X,
  CalendarDays
} from "lucide-react";
import Sidebar from "components/ui/Sidebar";
import Header from "components/ui/Header";
import PdfViewer from "components/ui/PdfViewer";

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const currentYear = new Date().getFullYear();

const EmployeePayrollPage = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { payrolls, fetchEmployeePayrolls, loading } = usePayrollStore();
  

  const [activeTab, setActiveTab] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  
  // New Filter States
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [selectedYear, setSelectedYear] = useState("");

  // States for PDF Preview (real backend payslip PDF, same as emailed)
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);

 useEffect(() => {
  // 🔹 Check karein ki user profile load ho chuki hai
  if (user) {
    // Priority: employee object ki ID, then fallback to user.id
    const targetId = user.employee?.id || user.id; 
    
    console.log("Calling Store with ID:", targetId);
    fetchEmployeePayrolls(targetId);
  }
}, [user, fetchEmployeePayrolls]);

  // ========== PAYSLIP PREVIEW / DOWNLOAD (real configured PDF) ==========
  // Open the preview: fetch the authoritative payslip PDF from the backend
  // (the exact configured format emailed to the employee) and show it inline.
  const openPayslip = async (payroll) => {
    setSelectedPayroll(payroll);
    setIsPreviewOpen(true);
    setPdfError(null);
    setPdfLoading(true);
    setPdfBlob(null);
    try {
      const blob = await financeService.getPayslipPdf(payroll.id);
      setPdfBlob(blob);
    } catch (err) {
      console.error("Failed to load payslip PDF:", err);
      setPdfError("Could not load your payslip. Please try again or contact HR.");
    } finally {
      setPdfLoading(false);
    }
  };

  const closePayslip = () => {
    setIsPreviewOpen(false);
    setPdfBlob(null);
    setSelectedPayroll(null);
  };

  const downloadPDF = () => {
    if (!pdfBlob || !selectedPayroll) return;
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Payslip_${monthNames[selectedPayroll.month - 1]}_${selectedPayroll.year}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // ========== ADVANCED FILTERING LOGIC ==========
  const filteredPayrolls = useMemo(() => {
    if (!payrolls || !Array.isArray(payrolls)) return [];

    return payrolls.filter((p) => {
      // 1. Status Filter (Paid/Pending)
      const statusMatch = activeTab === "All" || p.status?.toLowerCase() === activeTab.toLowerCase();
      
      // 2. Month Filter
      const monthMatch = selectedMonth === "All" || p.month === parseInt(selectedMonth);
      
      // 3. Year Filter
      const yearMatch = selectedYear === "" || p.year?.toString().includes(selectedYear);

      // 4. Search Input (Global Search)
      const mName = monthNames[p.month - 1] || "";
      const searchMatch = searchTerm === "" || 
                         mName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.year?.toString().includes(searchTerm) ||
                         p.id.toLowerCase().includes(searchTerm.toLowerCase());

      return statusMatch && monthMatch && yearMatch && searchMatch;
    });
  }, [payrolls, activeTab, searchTerm, selectedMonth, selectedYear]);

  // ========== DYNAMIC STATS ==========
  const stats = useMemo(() => {
    if (!payrolls || payrolls.length === 0) return { ytd: 0, lastNet: 0, pending: 0 };
    const ytd = payrolls.reduce((acc, curr) => acc + (Number(curr.netSalary) || 0), 0);
    const lastNet = Number(payrolls[0]?.netSalary) || 0;
    const pending = payrolls.filter(p => p.status !== 'paid').length;
    return { ytd, lastNet, pending };
  }, [payrolls]);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  return (
    <div className="flex h-screen bg-card overflow-hidden font-sans">
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        isMobileOpen={isMobileSidebarOpen}
        onMobileClose={() => setIsMobileSidebarOpen(false)}
      />
      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
 sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
 }`}>
        <Header onToggleSidebar={() => setIsMobileSidebarOpen(true)} />

        <main className="flex-1 mt-16 overflow-y-auto p-4 md:p-8 space-y-6 pb-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-foreground tracking-tight">Payroll History</h1>
              <p className="text-sm text-muted-foreground">Welcome, {user?.name || "Employee"}. View your earnings summary.</p>
            </div>
            <button className="flex items-center gap-2 px-4 py-2 bg-card border border-border text-xs font-semibold text-foreground hover:shadow-md transition-all rounded-xl">
              {/* <Download size={14} /> Download YTD Report */}
            </button>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard title="Year to Date" value={`₹${stats.ytd.toLocaleString('en-IN')}`} icon={<Banknote size={20} />} color="indigo" loading={loading} />
            <StatCard title="Last Payout" value={`₹${stats.lastNet.toLocaleString('en-IN')}`} icon={<FileText size={20} />} color="emerald" loading={loading} />
            <StatCard title="Pending Review" value={stats.pending} icon={<Loader2 size={20} />} color="amber" loading={loading} />
            <StatCard title="Current Year" value={currentYear} icon={<Calendar size={20} />} color="slate" loading={loading} />
          </div>

          {/* Filters & Data Container */}
          <div className="bg-card border border-border overflow-hidden ">
            
            {/* Toolbar Section */}
            <div className="p-5 border-b border-border space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                {/* Status Tabs */}
                <div className="flex bg-muted p-1 ">
                  {["All", "Paid", "Pending"].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-6 py-1.5 text-[11px] font-semibold uppercase transition-all ${
 activeTab === tab ? "bg-card text-primary shadow-sm" : "text-muted-foreground"
 }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                {/* Advanced Filter Inputs */}
                <div className="flex flex-wrap items-center gap-3">
                  {/* Month Select */}
                  <div className="relative">
                    <select 
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      className="appearance-none pl-3 pr-8 py-2 bg-muted/60 border border-border text-xs font-bold text-foreground focus:ring-2 focus:ring-ring outline-none cursor-pointer"
                    >
                      <option value="All">All Months</option>
                      {monthNames.map((m, i) => (
                        <option key={m} value={i + 1}>{m}</option>
                      ))}
                    </select>
                    <ChevronRight size={14} className="absolute right-2 top-1/2 -translate-y-1/2 rotate-90 text-muted-foreground/70 pointer-events-none" />
                  </div>

                  {/* Year Input */}
                  <div className="relative">
                    <input 
                      type="number" 
                      placeholder="Year (e.g. 2026)"
                      className="w-24 pl-3 pr-2 py-2 bg-muted/60 border border-border rounded-xl text-xs font-bold text-foreground focus:ring-2 focus:ring-ring outline-none"
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                    />
                  </div>

                  {/* Global Search */}
                  <div className="relative w-full md:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" size={14} />
                    <input
                      type="text"
                      placeholder="Search ID or Keyword..."
                      className="w-full pl-10 pr-4 py-2 bg-muted/60 border border-border rounded-xl text-xs font-bold focus:ring-2 focus:ring-ring outline-none"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  {/* Clear Filters */}
                  {(selectedMonth !== "All" || selectedYear !== "" || searchTerm !== "") && (
                    <button 
                      onClick={() => {setSelectedMonth("All"); setSelectedYear(""); setSearchTerm("");}}
                      className="p-2 text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                      title="Clear Filters"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-muted/50 border-b border-border">
                  <tr className="text-[11px] font-semibold text-muted-foreground/70 uppercase tracking-wide">
                    <th className="px-6 py-4">Billing Period</th>
                    <th className="px-6 py-4">Amount</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {loading ? (
                    <LoadingRows />
                  ) : filteredPayrolls.length > 0 ? (
                    filteredPayrolls.map((p) => (
                      <tr
                        key={p.id}
                        onClick={() => openPayslip(p)}
                        className="hover:bg-muted/60 transition-all cursor-pointer group"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center text-muted-foreground group-hover:bg-primary/90 group-hover:text-white transition-all">
                              <span className="text-[10px] font-semibold">{monthNames[p.month - 1]}</span>
                            </div>
                            <div>
                              <p className="text-sm font-bold text-foreground">{monthNames[p.month - 1]} {p.year}</p>
                              <p className="text-[9px] text-muted-foreground/70 font-bold uppercase tracking-tighter">REF: {p.id.slice(-8)}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-semibold text-foreground tracking-tight">₹{Number(p.netSalary).toLocaleString('en-IN')}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-tight ${
 p.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
 }`}>
                            {p.status === 'paid' ? 'Completed' : 'Processing'}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={(e) => { e.stopPropagation(); openPayslip(p); }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                          >
                            <FileText size={13} /> View
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="p-20 text-center">
                        <AlertCircle className="mx-auto text-slate-200 mb-2" size={48} />
                        <p className="text-muted-foreground/70 font-bold italic text-sm">No payroll records match these filters.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>

        {/* Preview Modal — renders the actual configured payslip PDF */}
        {isPreviewOpen && selectedPayroll && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-sidebar/60 backdrop-blur-sm">
            <div className="bg-card w-full max-w-3xl h-[88vh] rounded-lg overflow-hidden border border-border flex flex-col animate-in zoom-in-95 duration-200">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-border">
                <div>
                  <h2 className="font-display text-base font-semibold text-foreground tracking-tight">
                    Payslip · {monthNames[selectedPayroll.month - 1]} {selectedPayroll.year}
                  </h2>
                  <p className="text-xs text-muted-foreground">Net ₹{Number(selectedPayroll.netSalary).toLocaleString('en-IN')}</p>
                </div>
                <button onClick={closePayslip} className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors">
                  <X size={18} />
                </button>
              </div>

              {/* PDF body — custom viewer (no native browser PDF chrome) */}
              <div className="flex-1 overflow-hidden">
                {pdfLoading ? (
                  <div className="h-full flex flex-col items-center justify-center gap-3 text-muted-foreground bg-muted/40">
                    <Loader2 size={28} className="animate-spin" />
                    <p className="text-sm">Loading your payslip…</p>
                  </div>
                ) : pdfError ? (
                  <div className="h-full flex flex-col items-center justify-center gap-2 text-center px-6 bg-muted/40">
                    <AlertCircle size={28} className="text-error" />
                    <p className="text-sm text-muted-foreground">{pdfError}</p>
                    <button onClick={() => openPayslip(selectedPayroll)} className="mt-2 text-sm text-primary hover:underline">Retry</button>
                  </div>
                ) : pdfBlob ? (
                  <PdfViewer blob={pdfBlob} className="h-full" />
                ) : null}
              </div>

              {/* Footer */}
              <div className="px-5 py-3 border-t border-border flex justify-end gap-3">
                <button onClick={closePayslip} className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                  Close
                </button>
                <button
                  onClick={downloadPDF}
                  disabled={!pdfBlob}
                  className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-md hover:bg-primary/90 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <Download size={15} /> Download PDF
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper Components
const StatCard = ({ title, value, icon, color, loading }) => (
  <div className={`bg-card p-5 border border-border flex items-start justify-between`}>
    <div>
      <p className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wide">{title}</p>
      {loading ? <div className="h-7 w-24 bg-muted/60 animate-pulse rounded mt-2" /> : <h3 className="text-xl font-semibold text-foreground mt-1">{value}</h3>}
    </div>
    <div className={`p-2.5 bg-${color}-50 text-${color}-600 rounded-xl`}>{icon}</div>
  </div>
);

const DetailRow = ({ label, value, isRed }) => (
  <div className="flex justify-between border-b border-slate-50 pb-2">
    <span className="text-[10px] text-muted-foreground/70 font-semibold uppercase tracking-wide">{label}</span>
    <span className={`text-sm font-bold ${isRed ? 'text-red-500' : 'text-foreground'}`}>{value}</span>
  </div>
);

const LoadingRows = () => (
  <>
    {[1, 2, 3].map((i) => (
      <tr key={i} className="animate-pulse">
        <td className="px-6 py-4"><div className="h-12 w-full bg-muted/60 rounded-xl" /></td>
        <td className="px-6 py-4"><div className="h-6 w-20 bg-muted/60 rounded" /></td>
        <td className="px-6 py-4"><div className="h-6 w-24 bg-muted/60 rounded-full" /></td>
        <td className="px-6 py-4"><div className="h-8 w-8 bg-muted/60 rounded ml-auto" /></td>
      </tr>
    ))}
  </>
);

export default EmployeePayrollPage;